import { createContext, ReactNode, useContext, useMemo } from "react";

import { useRouter } from "next/router";
import {
  LangstonContentResponse,
  RawLangstonContentResponse,
} from "@/types/models/Content.interface";
import { PageViewModel } from "@/types/models/ContentPageViewModel.interface";
import { UseRestQueryResponse } from "@/remoteState/useRestQuery";
import useSingleContent from "@/content/useSingleContentQuery/useSingleContentQuery";
import useLangstonContentGet from "@/content/useLangstonContentGet";
import { ContentOutline } from "@/bundles/types/ContentOutline";

interface BundleDataContextValue {
  content: LangstonContentResponse | null | undefined;
  contentId: string;
  contentOutline: ContentOutline | undefined;
  currentContentId: string;
  contentLoading: boolean;
  contentRequestError: any | null;
  currentContentIdTreeRouteOrDefault: string;
  outlineIsLoading: boolean;
  treeRouteParam: string | undefined;
}

const BundleDataContext = createContext<BundleDataContextValue | undefined>(
  undefined,
);

function isEmtpyObject(obj: any): boolean {
  return obj && typeof obj === "object" && Object.keys(obj).length === 0;
}

/**
 * Sanitizes Langston's response data by validating fields and
 * replacing values that are too generic to type check.
 *
 * For now, this function validates the value of `page_viewmodel`,
 * making sure it is either `undefined` or satisfies the type
 * `PageViewModel`.
 */
function sanitizeLanstonResponse(
  data: RawLangstonContentResponse | null | undefined,
): LangstonContentResponse | null | undefined {
  if (!data || data === undefined) {
    return data;
  } else {
    const pageVm = isEmtpyObject(data.page_viewmodel)
      ? undefined
      : (data.page_viewmodel as PageViewModel);
    return { ...data, page_viewmodel: pageVm };
  }
}

export const BundleDataProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const { query } = router;
  const { contentId, tree: treeRouteParam } = query as {
    contentId: string;
    tree?: string;
  };

  const { data: contentOutline, isLoading: outlineIsLoading } =
    useSingleContent<ContentOutline>({
      contentId,
      queryParams: {
        viewmodel_type: "headless_tree",
      },
    });

  const currentContentIdTreeRouteOrDefault = treeRouteParam ?? contentId;
  // With the headless tree data structure in mind, `currentContentId` is the
  // content ID of a child content defined by `tree`. If we don't have the tree
  // of a selected child content, `currentContentId` defaults to `contentId`.
  const currentContentId =
    contentOutline?.items?.[currentContentIdTreeRouteOrDefault]?.object_id ??
    contentId;

  const {
    data: content,
    error: contentRequestError,
    loading: contentLoading,
  }: UseRestQueryResponse<RawLangstonContentResponse> = useLangstonContentGet<RawLangstonContentResponse>(
    currentContentId as string,
  );

  // Memoize context value so that children don’t re-render unnecessarily
  const value = useMemo<BundleDataContextValue>(
    () => ({
      content: sanitizeLanstonResponse(content),
      contentId,
      contentOutline,
      contentLoading,
      contentRequestError,
      currentContentId,
      currentContentIdTreeRouteOrDefault,
      outlineIsLoading,
      treeRouteParam,
    }),
    [
      content,
      contentId,
      contentLoading,
      contentRequestError,
      contentOutline,
      currentContentId,
      currentContentIdTreeRouteOrDefault,
      outlineIsLoading,
      treeRouteParam,
    ],
  );

  return (
    <BundleDataContext.Provider value={value}>
      {children}
    </BundleDataContext.Provider>
  );
};

export const useBundleDataContext = () => {
  const context = useContext(BundleDataContext);
  if (!context) {
    throw new Error(
      "useBundleDataContext must be used within a BundleDataProvider",
    );
  }
  return context;
};
